import { memo, useEffect, useState } from "react";
import {
  resolveClientHotLeadReason,
  type ClientBotWallet,
  type ClientBotWithdrawal,
  type ClientHotLeadReason
} from "@tg/shared";
import { useRealtimeStore } from "../../lib/store.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";
import { formatShortDateTime, type TimeZonePref } from "../../lib/datetime.js";
import { trackerApi } from "../../lib/tracker-api.js";

// Блок «Данные из бота» в табе «Инфо» (запрос №24): баланс прибыли клиента
// в боте, заявка на вывод и причина статуса «🔥 Горячий» у клиента без
// депозита. Отдельный компонент: свой fetch и свои подписки на стор не
// перерисовывают всю боковую панель.

const WITHDRAWAL_STATUS_LABELS: Record<string, string> = {
  pending: "В обработке",
  processing: "В обработке",
  approved: "Одобрена",
  paid: "Выплачена",
  rejected: "Отклонена"
};

const HOT_REASON_LABELS: Record<ClientHotLeadReason, string> = {
  balance: "баланс в боте",
  withdrawal: "заявка на вывод",
  both: "баланс в боте и заявка на вывод"
};

const amountFormat = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });

const formatAmount = (amount: number, currency: string): string =>
  `${amountFormat.format(amount)}${currency ? ` ${currency}` : ""}`;

const describeWithdrawal = (withdrawal: ClientBotWithdrawal, timeZone: TimeZonePref): string => {
  const status = WITHDRAWAL_STATUS_LABELS[withdrawal.status] ?? (withdrawal.status || "—");
  const parts = [status, formatAmount(withdrawal.amount, withdrawal.currency)];
  const at = withdrawal.createdAt ? formatShortDateTime(withdrawal.createdAt, timeZone) : "";
  if (at) {
    parts.push(`от ${at}`);
  }
  return parts.join(" · ");
};

type WalletState =
  | { kind: "loading" }
  | { kind: "ready"; telegramId: number; wallets: ClientBotWallet[] }
  | { kind: "error" };

export const ClientBotWalletPanel = memo(function ClientBotWalletPanel({
  telegramId,
  wsUrl
}: {
  telegramId: number;
  wsUrl: string;
}) {
  const timeZone: TimeZonePref = useUiPrefs((s) => s.timeZone);
  const deposit = useRealtimeStore((state) => state.clientDeposits[telegramId]);
  const lead = useRealtimeStore((state) => state.clientHotLeads[telegramId]);
  const settings = useRealtimeStore((state) => state.clientStatusSettings);
  const [state, setState] = useState<WalletState>({ kind: "loading" });

  // Перечитываем при смене чата и когда синхронизация принесла новые
  // данные по этому клиенту (баланс/заявка поменялись) — сумма актуальная.
  const leadKey = lead ? `${lead.balanceQualified}:${lead.hasActiveWithdrawal}` : "none";
  useEffect(() => {
    let cancelled = false;
    // Обновление того же клиента — старые суммы остаются на экране до ответа;
    // другой клиент — чужие суммы не показываем.
    setState((previous) =>
      previous.kind === "ready" && previous.telegramId === telegramId ? previous : { kind: "loading" }
    );
    trackerApi
      .getBotWallets(wsUrl, telegramId)
      .then((result) => {
        if (!cancelled) setState({ kind: "ready", telegramId, wallets: result.wallets ?? [] });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [telegramId, wsUrl, leadKey]);

  const reason = !deposit && settings ? resolveClientHotLeadReason(lead, settings) : null;

  if (state.kind === "error") {
    // Админка недоступна — блок не мешает работе; причина статуса всё равно
    // видна, если клиент «Горячий».
    return reason ? (
      <div className="tracker-panel bot-wallet-panel">
        <div className="tracker-panel__head">
          <span aria-hidden="true">🔥</span>
          <span className="tracker-panel__title">Данные из бота</span>
        </div>
        <div className="bot-wallet-panel__row">
          <span className="bot-wallet-panel__label">Причина статуса «Горячий»</span>
          <span className="bot-wallet-panel__value">{HOT_REASON_LABELS[reason]}</span>
        </div>
        <div className="tracker-panel__error">Не удалось загрузить баланс из бота</div>
      </div>
    ) : null;
  }
  if (state.kind === "loading" || state.telegramId !== telegramId) {
    return null;
  }
  if (state.wallets.length === 0 && !reason) {
    // Клиента нет ни в одном боте с балансом — показываем коротко, чтобы саппорт
    // видел, что проверка была, а не что блок «не загрузился».
    return (
      <div className="tracker-panel bot-wallet-panel">
        <div className="tracker-panel__head">
          <i className="ic ic-card" aria-hidden="true" />
          <span className="tracker-panel__title">Данные из бота</span>
        </div>
        <span className="tracker-panel__hint">Клиента нет в ботах с балансом</span>
      </div>
    );
  }

  const showBotNames = state.wallets.length > 1;
  return (
    <div className="tracker-panel bot-wallet-panel">
      <div className="tracker-panel__head">
        <i className="ic ic-card" aria-hidden="true" />
        <span className="tracker-panel__title">Данные из бота</span>
        {!showBotNames && state.wallets[0] ? (
          <span className="tracker-panel__card" title="Бот, в котором клиент">
            {state.wallets[0].botName}
          </span>
        ) : null}
      </div>
      {state.wallets.map((wallet) => (
        <div key={wallet.botId} className="bot-wallet-panel__wallet">
          {showBotNames ? (
            <div className="bot-wallet-panel__bot">
              {wallet.botName}
              {wallet.botIsArchived ? " · в архиве" : !wallet.botIsActive ? " · выключен" : ""}
            </div>
          ) : null}
          <div className="bot-wallet-panel__row">
            <span className="bot-wallet-panel__label">Баланс прибыли в боте</span>
            <span className="bot-wallet-panel__value">
              {formatAmount(wallet.balance, wallet.currency)}
              {wallet.coins !== null ? (
                <span className="bot-wallet-panel__sub"> · {amountFormat.format(wallet.coins)} коинов</span>
              ) : null}
            </span>
          </div>
          <div className="bot-wallet-panel__row">
            <span className="bot-wallet-panel__label">Заявка на вывод</span>
            <span
              className={
                wallet.activeWithdrawal
                  ? "bot-wallet-panel__value bot-wallet-panel__value--active"
                  : "bot-wallet-panel__value"
              }
            >
              {!wallet.supportsWithdrawals
                ? "Нет — в этом боте нет вывода"
                : wallet.activeWithdrawal
                  ? `Есть · ${describeWithdrawal(wallet.activeWithdrawal, timeZone)}`
                  : wallet.lastWithdrawal
                    ? `Нет активной · последняя: ${describeWithdrawal(wallet.lastWithdrawal, timeZone)}`
                    : "Нет"}
            </span>
          </div>
        </div>
      ))}
      {reason ? (
        <div className="bot-wallet-panel__row bot-wallet-panel__row--reason">
          <span className="bot-wallet-panel__label">Причина статуса «🔥 Горячий»</span>
          <span className="bot-wallet-panel__value">{HOT_REASON_LABELS[reason]}</span>
        </div>
      ) : null}
    </div>
  );
});
